/**
 * OFE Production Planner — shows per-unit items for a vendor or project.
 * Route params: vendorId OR projectId (from URL path).
 * Column visibility enforced by role-based Pydantic views on the backend.
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch, ofePatch } from './useOFE';

const STATUS_COLORS = {
  not_started: 'bg-gray-700 text-gray-300',
  in_fab: 'bg-blue-900/40 text-blue-300',
  in_production: 'bg-yellow-900/40 text-yellow-300',
  fab_complete: 'bg-cyan-900/40 text-cyan-300',
  shipped: 'bg-purple-900/40 text-purple-300',
  in_transit: 'bg-orange-900/40 text-orange-300',
  delivered: 'bg-green-900/40 text-green-300',
  installed: 'bg-green-600/40 text-green-200',
  rejected: 'bg-red-900/40 text-red-400',
};

export default function OFEProductionPlanner() {
  const { vendorId, projectId } = useParams();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const queryParts = [];
  if (vendorId && vendorId !== 'ALL') queryParts.push(`vendor_id=${vendorId}`);
  if (projectId && projectId !== 'ALL') queryParts.push(`project=${projectId}`);
  if (statusFilter) queryParts.push(`status=${statusFilter}`);
  const query = queryParts.length ? `?${queryParts.join('&')}&limit=200` : '?limit=200';

  const { data: items, loading, error, reload } = useOFEFetch(`/ofe/production${query}`, user, [vendorId, projectId, statusFilter]);

  const filtered = (items || []).filter(i =>
    !search || (i.equipment_type || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.serial_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.tag_number || '').toLowerCase().includes(search.toLowerCase())
  );

  const title = vendorId && vendorId !== 'ALL'
    ? `Production Planner — Vendor`
    : projectId && projectId !== 'ALL'
      ? `Production Planner — ${projectId}`
      : 'Production Planner — All';

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="text-xl font-bold text-white">{title}</h1>
        <span className="text-gray-400 text-sm">{filtered.length} items</span>
        <div className="ml-auto flex gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search serial / tag / type..."
            className="bg-gray-800 border border-gray-600 text-white px-3 py-1.5 rounded text-sm w-56" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-gray-800 border border-gray-600 text-white px-3 py-1.5 rounded text-sm">
            <option value="">All statuses</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="text-gray-400">Loading...</div>}
      {error && <div className="text-red-400">Error: {error}</div>}

      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-auto">
        <table className="w-full text-sm min-w-max">
          <thead className="bg-gray-900 text-gray-400 sticky top-0">
            <tr>
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3">Equipment Type</th>
              <th className="text-left px-4 py-3">Serial #</th>
              <th className="text-left px-4 py-3">Tag #</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Plan Ship</th>
              <th className="text-left px-4 py-3">Act Ship</th>
              <th className="text-left px-4 py-3">Delivery Plan</th>
              <th className="text-left px-4 py-3">Vendor PM</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filtered.map(item => (
              <tr key={item.id} className="hover:bg-gray-750 transition-colors">
                <td className="px-4 py-2 font-mono text-xs text-blue-400">{item.project_allocation}</td>
                <td className="px-4 py-2 text-white">{item.equipment_type}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-300">{item.serial_number || '—'}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-300">{item.tag_number || '—'}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[item.production_status] || 'bg-gray-700 text-gray-300'}`}>
                    {(item.production_status || '').replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-300 text-xs">{item.mfg_ship_date_plan || '—'}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={item.mfg_ship_date_actual ? 'text-green-400' : 'text-gray-500'}>
                    {item.mfg_ship_date_actual || '—'}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-300 text-xs">{item.delivery_date_plan || '—'}</td>
                <td className="px-4 py-2 text-gray-300 text-xs">{item.vendor_pm || '—'}</td>
                <td className="px-4 py-2">
                  <Link to={`/ofe/production/item/${item.id}`} className="text-blue-400 hover:text-blue-300 text-xs">→</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No items found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
